import { useEffect, useMemo, useState, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, createGlobalStyle } from "styled-components";
import { HelmetProvider } from "react-helmet-async";
import NavBar from "./components/NavBar";
import SkipLink from "./components/SkipLink";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import { lightTheme, darkTheme } from "./theme/theme";


const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    transition: background 0.3s ease, color 0.3s ease;
  }

  html { scroll-behavior: smooth; }
  * { box-sizing: border-box; }
  a { color: inherit; text-decoration: none; }
`;

const sections = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how" },
  { label: "Downloads", href: "#downloads" },
  { label: "FAQ", href: "#faq" }
];

const getPreferredTheme = () => {
  if (typeof window === "undefined") {
    return "light";
  }
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

function App() {
  const [themeName, setThemeName] = useState(getPreferredTheme);
  const [isUserOverride, setIsUserOverride] = useState(false);

  const theme = useMemo(() => (themeName === "light" ? lightTheme : darkTheme), [themeName]);

  const handleToggleTheme = () => {
    setThemeName((prev) => {
      const next = prev === "light" ? "dark" : "light";
      const systemPref = getPreferredTheme();
      setIsUserOverride(next !== systemPref);
      return next;
    });
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mql = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event) => {
      if (!isUserOverride) {
        setThemeName(event.matches ? "dark" : "light");
      }
    };

    if (mql.addEventListener) {
      mql.addEventListener("change", handleChange);
    } else {
      // Safari < 14
      mql.addListener(handleChange);
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", handleChange);
      } else {
        mql.removeListener(handleChange);
      }
    };
  }, [isUserOverride]);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <BrowserRouter>
          <SkipLink />
          <NavBar sections={sections} themeName={themeName} onToggleTheme={handleToggleTheme} />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </Suspense>
          <Footer />
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
